import { createStyles, Divider, makeStyles, Theme } from '@material-ui/core';
import { useLinked } from '@type-r/react';
import * as React from 'react';
import { CompanyModel, RelationshipModel } from 'api';
import { Section, Subsection, FormInput, Columns, Column, PickItem, ReadOnly } from 'lqx-react';
import { InputGroup } from '../common';
import { EditIdentifiers } from './identifiers' ;
import { Collection } from '@type-r/models';
import { CollapsableSection } from 'lqx-react/lib';
import { TopSection } from 'lqx-react/src';
import { WorkflowSection } from './workflow-section';
import { CustomerNotificationsSection, SupplierNotificationsSection } from './notifications-section';

export const RelationshipTopSection = ({ relationship, companies } : {
    relationship: RelationshipModel, companies: Collection<CompanyModel>}) => {

    return (
        <>
            <EditRelationshipInformation
                relationship={relationship}
                companies={companies}
            />
            <EditRelationshipAttributes
                relationship={relationship}
            />
            <TopSection
                id="notifications"
                title="Notifications">
                <CustomerNotificationsSection relationship={relationship} />
                <SupplierNotificationsSection relationship={relationship} />
            </TopSection>
        </>
    )
}

const EditRelationshipInformation = ({ relationship, companies }: {
    relationship: RelationshipModel, companies: Collection<CompanyModel>}) => {
    const classes = useStyles();
    const $customerPicked = useLinked<object>( null );
    const $supplierPicked = useLinked<object>( null );
    const $customer = useLinked<CompanyModel>( null );
    const supplier = relationship.Supplier;
    const customer = relationship.Customer;

    return ( <>
                    <ReadOnly.Provider value={ !!relationship.lqxId }>
                        <TopSection
                            id="relationship-information"
                            title="Relationship Information"
                        >
                            <CollapsableSection title="Participants">
                            {
                                relationship.ShowSupplier && <Subsection title="Supplier">
                                    <Columns>
                                        <Column>
                                            {
                                                !relationship.id ?
                                                <PickItem
                                                    aria-label="Customer"
                                                    $value = {$supplierPicked}
                                                    items = {companies}
                                                    onChange = {(newValue) => {relationship.Supplier.companyId = newValue.id; }}
                                                    format={ ( x: any ) => x ? x.name : '' }
                                                    filter={ ( x, filter ) => x.name.toLowerCase().indexOf( filter.toLowerCase() ) >= 0 }
                                                >
                                                    { x => x.name }
                                                </PickItem>
                                                :
                                                <FormInput disabled label="Supplier name" value={supplier.companyName} aria-label="Supplier Name"/>
                                            }
                                        </Column>
                                        <Column>
                                            <FormInput
                                                $value={relationship.$.supplierExternalId}
                                                label="Supplier External Entity ID"
                                            />
                                            <FormInput
                                                $value={supplier.$.externalRelationshipId}
                                                label="Supplier Relationship ID"
                                            />
                                            <FormInput
                                                $value={supplier.$.mep}
                                                label="Parent Site"
                                            />
                                            <FormInput
                                                $value={supplier.$.subMep}
                                                label="Sub Site"
                                            />
                                        </Column>
                                    </Columns>
                                </Subsection>
                            }
                            {
                                relationship.ShowCustomer &&
                                <Subsection title="Customer">
                                    <Columns>
                                        <Column>
                                        {
                                            !relationship.id ?
                                                <PickItem
                                                    aria-label="Customer"
                                                    $value={$customerPicked}
                                                    items = {companies}
                                                    onChange = {(newValue) => {relationship.Customer.companyId = newValue.id; }}
                                                    format={ ( x: any ) => x ? x.name : '' }
                                                    filter={ ( x, filter ) => x.name.toLowerCase().indexOf( filter.toLowerCase() ) >= 0 }
                                                >
                                                    { x => x.name }
                                                </PickItem>
                                            :
                                            <FormInput disabled label="Customer name" value={customer.companyName} aria-label="Customer Name" />
                                        }

                                        </Column>
                                        <Column>
                                            <FormInput
                                                $value={customer.$.externalRelationshipId}
                                                label="Customer Relationship ID"
                                            />
                                        </Column>
                                    </Columns>
                                </Subsection>
                            }
                        </CollapsableSection>
                    </TopSection >
                </ReadOnly.Provider>
                <WorkflowSection relationship={relationship}/>
                <EditIdentifiers relationship={relationship}/>
            </>
    )
}

const EditRelationshipAttributes = ({ relationship }: { relationship: RelationshipModel}) => {
    return (
        <ReadOnly.Provider value={ !!relationship.lqxId }>
            <InputGroup>
                <Section
                    id="relationship-attributes"
                    title="Relationship Attributes"
                >
                    <Subsection
                        title="Contract Terms"
                    >
                        <Columns>
                            <Column>
                                <FormInput
                                    disabled
                                    label="Relationship Contract Date"
                                />
                                <FormInput
                                    $value={relationship.$.currency}
                                    required
                                    label="Currency Code"
                                />
                                <FormInput
                                    disabled
                                    label="Supplier Wire Fee Sharing"
                                />
                                <FormInput
                                    disabled
                                    label="Types of Goods/Services"
                                />
                                <FormInput positive integer
                                    disabled
                                    label="Payment Period (terms net days)"
                                />
                                <FormInput
                                    disabled
                                    label="Late Fee Interest"
                                />
                                <FormInput
                                    $value={relationship.$.governingLaw}
                                    required
                                    label="Governing Law(s)"
                                />
                                <FormInput
                                    disabled
                                    label="Supplier Wire Fee ($)"
                                />
                                <FormInput
                                    disabled
                                    label="Payment Method"
                                />
                            </Column>
                            <Column>
                                <FormInput
                                    $value={relationship.$.paymentTerms}
                                    required
                                    label="Payment Terms"
                                />
                                <FormInput positive integer
                                    $value={relationship.$.earlyPayDiscountPercentage}
                                    required
                                    label="Early Pay Discount Percentage"
                                />
                                <FormInput positive integer
                                    $value={relationship.$.creditLimit}
                                    label="Credit Limit"
                                />
                                <FormInput
                                    $value={relationship.$.incoTerms}
                                    required
                                    label="Inco Terms"
                                />
                                <FormInput positive integer
                                    $value={relationship.$.earlyPayThreshold}
                                    required
                                    label="Early Pay Threshold (in days)"
                                />
                                <FormInput
                                    disabled
                                    label="Payment Recourse"
                                />
                                <FormInput positive integer
                                    disabled
                                    required
                                    label="Discount Period"
                                />
                                <FormInput
                                    disabled
                                    label="Settlement Pattern"
                                />
                            </Column>
                        </Columns>
                    </Subsection>
                    <Divider />
                    {/* <EditContactAddress
                        title="Bill To"
                        contact={relationship.contact}
                        relationship={relationship}
                    /> */}
                </Section>
            </InputGroup>
        </ReadOnly.Provider>
    )
}


const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        empty: {}
    }),
);
